import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { getPublicCompany, submitVisitorLead } from "@/lib/marketplace.functions";
import { getCompanyTrust } from "@/lib/proofs.functions";
import { TrustBadges, TrustScorePill } from "@/components/TrustBadges";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import {
  Activity,
  ArrowLeft,
  BadgeCheck,
  BarChart3,
  Check,
  Database,
  Globe2,
  Mail,
  MessageSquare,
  Network,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";
import { MarketingLayout as Layout, useHydratedTranslation } from "@/components/marketing/Layout";
import { useTranslatable, TranslateControl } from "@/components/TranslatableContent";
import { Markdown, SourceChips, splitFollowups, messageSources } from "@/components/TwinChat";
import { buildSeo, SITE_URL, SITE_NAME } from "@/lib/seo";
import { toast } from "sonner";

export const Route = createFileRoute("/marketplace/$id")({
  loader: async ({ params }) => {
    try {
      const r = await getPublicCompany({ data: { id: params.id } });
      return { company: r.company };
    } catch {
      return {
        company: null as null | {
          id: string;
          name: string;
          description: string | null;
          industry: string | null;
          website: string | null;
        },
      };
    }
  },
  head: ({ params, loaderData }) => {
    const c = loaderData?.company;
    const name = c?.name ?? "AI Twin";
    const desc = (
      c?.description ??
      `Chat with ${name}'s AI twin on Reality Twin Network. Ask about the company, partnerships, and opportunities.`
    ).slice(0, 158);
    const title = `${name} — Reality Twin Network`.slice(0, 60);
    const seo = buildSeo({
      path: `/marketplace/${params.id}`,
      title,
      description: desc,
      type: "profile",
      noindex: !c,
    });
    const scripts = c
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              name: `${c.name} AI Twin`,
              description: c.description ?? undefined,
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: `${SITE_URL}/marketplace/${c.id}`,
              publisher: { "@type": "Organization", name: "Ruznic Marketing", url: SITE_URL },
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free to chat",
              },
              ...(c.website ? { sameAs: [c.website] } : {}),
              provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            }),
          },
        ]
      : [];
    return { meta: seo.meta, links: seo.links, scripts };
  },
  component: Page,
});

type ParsedSection = { label: string; text?: string; items?: string[]; chips?: boolean };
type Parsed = { intro: string; sections: ParsedSection[] };

const CHIP_LABELS = new Set(["Audience", "Goals", "Features", "Keywords", "Topics", "Services"]);
const INTERNAL_LABELS = new Set(["Handoff"]);
const PUBLIC_LABELS: Record<string, string> = {
  Audience: "Market signals",
  Goals: "Twin outcomes",
  Features: "Capability surface",
  Keywords: "Search intelligence",
  Topics: "Knowledge domains",
  Services: "Commercial surface",
  Tone: "Voice profile",
};

function parseDescription(raw: string | null | undefined): Parsed {
  if (!raw) return { intro: "", sections: [] };
  const text = raw.replace(/\r\n/g, "\n").trim();
  // Split on "Label:" patterns (Audience:, Goals:, Tone:, Handoff:, etc.)
  const re = /(?:^|\s)([A-Z][A-Za-z ]{2,24}):\s+/g;
  const matches: { label: string; index: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    matches.push({
      label: m[1].trim(),
      index: m.index + (m[0].length - m[1].length - 2),
      end: re.lastIndex,
    });
  }
  if (matches.length === 0) return { intro: text, sections: [] };
  const intro = text.slice(0, matches[0].index).trim();
  const sections: ParsedSection[] = matches.map((mm, i) => {
    const next = matches[i + 1]?.index ?? text.length;
    const body = text.slice(mm.end, next).trim().replace(/\.$/, "");
    if (CHIP_LABELS.has(mm.label)) {
      const items = body
        .split(/,|·|•|\n/)
        .map((s) => s.trim())
        .filter((s) => s.length > 1 && s.length < 40)
        .slice(0, 14);

      return { label: mm.label, items, chips: true };
    }
    return { label: mm.label, text: body };
  });
  return { intro, sections };
}

function compactNumber(value: unknown) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n >= 1000) return `${Math.round(n / 100) / 10}k`;
  return String(n);
}

function readinessKey(score: unknown): "networkGrade" | "commercial" | "training" | "foundation" {
  const n = Number(score ?? 0);
  if (n >= 85) return "networkGrade";
  if (n >= 65) return "commercial";
  if (n >= 35) return "training";
  return "foundation";
}

function buildCompanyBrief(
  company: any,
  fallbacks: { outcomes: string[]; capabilities: string[] },
) {
  const parsed = parseDescription(company.description);
  const paragraphs = parsed.intro
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const publicSections = parsed.sections
    .filter((s) => !INTERNAL_LABELS.has(s.label))
    .map((s) => ({ ...s, label: PUBLIC_LABELS[s.label] ?? s.label }));
  const chips = publicSections.flatMap((s) => s.items ?? []).filter(Boolean);
  const primarySignals = Array.from(new Set(chips)).slice(0, 10);
  const outcomes = parsed.sections.find((s) => s.label === "Goals")?.items?.slice(0, 5) ?? [];
  const capabilities = Array.from(
    new Set([
      ...(parsed.sections.find((s) => s.label === "Features")?.items ?? []),
      ...(parsed.sections.find((s) => s.label === "Services")?.items ?? []),
      ...(Array.isArray(company.seeking) ? company.seeking : []),
    ]),
  ).slice(0, 6);

  return {
    paragraphs,
    publicSections,
    primarySignals: primarySignals.length
      ? primarySignals
      : [company.industry, company.country, company.size].filter(Boolean),
    outcomes: outcomes.length ? outcomes : fallbacks.outcomes,
    capabilities: capabilities.length ? capabilities : fallbacks.capabilities,
  };
}

function Page() {
  const t = useHydratedTranslation();
  const { id } = Route.useParams();
  const fetchCompany = useServerFn(getPublicCompany);
  const sendLead = useServerFn(submitVisitorLead);
  const fetchTrust = useServerFn(getCompanyTrust);
  const [company, setCompany] = useState<any>(null);
  const [trust, setTrust] = useState<{ proofs: any[]; score: number }>({ proofs: [], score: 0 });
  const [error, setError] = useState<string | null>(null);
  // Stable per-visit conversation id so the server can thread + persist turns.
  const [conversationId] = useState(() => crypto.randomUUID());
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadSending, setLeadSending] = useState(false);

  useEffect(() => {
    fetchCompany({ data: { id } })
      .then((r) => setCompany(r.company))
      .catch((e) => setError(e.message ?? "Not found"));
    fetchTrust({ data: { companyId: id } })
      .then((r) => setTrust(r as any))
      .catch(() => {});
  }, [fetchCompany, fetchTrust, id]);

  const { messages, sendMessage, status } = useChat({
    transport: new DefaultChatTransport({ api: "/api/chat" }),
  });
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // One-click AI translation of the company's own description. Translating
  // the raw text upstream means the parsed brief, chips and signals all
  // follow the chosen language.
  const descTranslate = useTranslatable([company?.description]);

  if (error)
    return (
      <Layout>
        <div className="p-24 text-center text-muted-foreground">{t("routes.profile.notFound")}</div>
      </Layout>
    );
  if (!company)
    return (
      <Layout>
        <div className="p-24 text-center text-muted-foreground">{t("routes.profile.loading")}</div>
      </Layout>
    );

  function ask(text: string) {
    sendMessage({ text }, { body: { companyId: company.id, conversationId } });
  }
  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim()) return;
    ask(input.trim());
    setInput("");
  }

  async function submitLead(e: React.FormEvent) {
    e.preventDefault();
    if (!lead.name.trim() || !lead.email.trim()) return;
    setLeadSending(true);
    try {
      await sendLead({
        data: {
          companyId: company.id,
          conversationId,
          name: lead.name,
          email: lead.email,
          message: lead.message || undefined,
        },
      });
      setLeadSent(true);
      toast.success(t("routes.profile.leadSuccess", { name: company.name }));
    } catch (err: any) {
      toast.error(err?.message ?? t("routes.profile.leadError"));
    } finally {
      setLeadSending(false);
    }
  }

  const isLoading = status === "submitted" || status === "streaming";

  const initial = (company.name?.[0] ?? "?").toUpperCase();
  const logo = company.logo_url as string | null | undefined;
  const effectiveDescription = descTranslate.display[0] || company.description;
  const brief = buildCompanyBrief(
    { ...company, description: effectiveDescription },
    {
      outcomes: t("routes.profile.defaultOutcomes", { returnObjects: true }) as unknown as string[],
      capabilities: t("routes.profile.defaultCapabilities", {
        returnObjects: true,
      }) as unknown as string[],
    },
  );
  const readiness = Math.max(0, Math.min(100, Number(company.ai_score ?? 0)));
  const readinessLabel = t(`routes.profile.readiness.${readinessKey(company.ai_score)}`);
  // Behavioural reputation — only present once the company has enough history,
  // so a brand-new twin isn't judged on one interaction.
  const rep = (company as any).reputation as {
    responseRate: number | null;
    medianResponseHours: number | null;
    completionRate: number | null;
  } | null;
  const repHours = rep?.medianResponseHours ?? null;
  const repTime =
    repHours === null
      ? null
      : repHours < 1
        ? "< 1h"
        : repHours < 24
          ? `~${Math.round(repHours)}h`
          : `~${Math.round(repHours / 24)}d`;
  const reputationCards = rep
    ? [
        rep.responseRate !== null && {
          label: t("routes.profile.metricResponseRate", { defaultValue: "Response rate" }),
          value: `${rep.responseRate}%`,
          detail: t("routes.profile.metricResponseRateSub", {
            defaultValue: "Inbound requests acted on",
          }),
          icon: Activity,
        },
        repTime !== null && {
          label: t("routes.profile.metricResponseTime", { defaultValue: "Response time" }),
          value: repTime,
          detail: t("routes.profile.metricResponseTimeSub", {
            defaultValue: "Typical first reply",
          }),
          icon: Activity,
        },
        rep.completionRate !== null && {
          label: t("routes.profile.metricCompletion", { defaultValue: "Completion rate" }),
          value: `${rep.completionRate}%`,
          detail: t("routes.profile.metricCompletionSub", {
            defaultValue: "Engaged work finished",
          }),
          icon: Check,
        },
      ].filter(Boolean)
    : [];

  const metricCards = [
    ...(reputationCards as {
      label: string;
      value: string;
      detail: string;
      icon: typeof Activity;
    }[]),
    {
      label: t("routes.profile.metricReadiness"),
      value: typeof company.ai_score === "number" ? `${company.ai_score}` : "—",
      detail: readinessLabel,
      icon: Activity,
    },
    {
      label: t("routes.profile.metricPages"),
      value: compactNumber(company.pages_indexed),
      detail:
        Number(company.pages_indexed ?? 0) > 0
          ? t("routes.profile.websiteIntel")
          : t("routes.profile.crawlPending"),
      icon: Globe2,
    },
    {
      label: t("routes.profile.metricDocs"),
      value: compactNumber(company.documents_indexed),
      detail:
        Number(company.documents_indexed ?? 0) > 0
          ? t("routes.profile.knowledgeFiles")
          : t("routes.profile.noFiles"),
      icon: Database,
    },
    {
      label: t("routes.profile.metricKb"),
      value: compactNumber(company.knowledge_size),
      detail:
        Number(company.knowledge_size ?? 0) > 0
          ? t("routes.profile.indexedSignals")
          : t("routes.profile.profileKnowledge"),
      icon: BarChart3,
    },
  ];
  const operatingStack = [
    {
      label: t("routes.profile.discoveryLayer"),
      value: t("routes.profile.publicProfile"),
      icon: Network,
    },
    {
      label: t("routes.profile.commercialIntent"),
      value: brief.outcomes.slice(0, 2).join(" · "),
      icon: Target,
    },
    {
      label: t("routes.profile.trustState"),
      value: company.twin_status ?? "active",
      icon: ShieldCheck,
    },
  ];
  const starterPrompts = (
    t("routes.profile.starters", { returnObjects: true }) as unknown as string[]
  ).map((s) => s.replace(/\{\{name\}\}/g, company.name));

  return (
    <Layout>
      {/* Hero band */}
      <section className="relative border-b border-border bg-cream">
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 80% at 20% 0%, oklch(0.92 0.05 168 / 0.6), transparent 60%), radial-gradient(50% 70% at 90% 20%, oklch(0.92 0.05 290 / 0.5), transparent 60%)",
          }}
        />
        <div className="relative mx-auto max-w-6xl px-6 sm:px-10 pt-10 pb-14">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t("routes.profile.back")}
          </Link>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-8">
            <div className="relative shrink-0">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-foreground/10 to-transparent blur-xl" />
              <div
                className={`relative h-24 w-24 sm:h-28 sm:w-28 overflow-hidden rounded-3xl flex items-center justify-center font-serif text-4xl sm:text-5xl shadow-[var(--shadow-card)] ${
                  logo ? "bg-white border border-border" : "bg-foreground text-background"
                }`}
              >
                {logo ? (
                  <img
                    src={logo}
                    alt={`${company.name} logo`}
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  initial
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-mono">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                {t("routes.profile.live")}
                {(company as any).verified && (
                  <>
                    <span className="text-border">·</span>
                    <span className="inline-flex items-center gap-1 text-emerald-600 normal-case tracking-normal">
                      <BadgeCheck className="h-3.5 w-3.5" /> {t("routes.profile.verified")}
                    </span>
                  </>
                )}
              </div>
              <h1 className="mt-3 font-serif text-[44px] sm:text-[60px] leading-[0.98] tracking-[-0.028em]">
                {company.name}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
                {company.industry && (
                  <span className="rounded-full border border-border bg-card px-3 py-1">
                    {company.industry}
                  </span>
                )}
                {company.country && (
                  <span className="rounded-full border border-border bg-card px-3 py-1">
                    {company.country}
                  </span>
                )}
                {company.website && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full border border-border bg-card px-3 py-1 hover:bg-foreground hover:text-background transition"
                  >
                    {t("routes.profile.visitWebsite")}
                  </a>
                )}
                {trust.score > 0 && <TrustScorePill score={trust.score} />}
              </div>
              {trust.proofs.length > 0 && (
                <div className="mt-3">
                  <TrustBadges proofs={trust.proofs} compact />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 py-12 lg:py-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.02fr)_minmax(420px,0.98fr)] lg:items-start">
            <div className="space-y-8">
              <article className="border-y border-border py-7">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="font-mono-eyebrow">{t("routes.profile.briefEyebrow")}</div>
                  <div className="flex items-center gap-3">
                    <TranslateControl state={descTranslate} />
                    <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
                      {company.industry ?? t("routes.profile.companyFallback")} ·{" "}
                      {company.country ?? t("routes.profile.networkFallback")}
                    </div>
                  </div>
                </div>
                <h2 className="mt-5 max-w-3xl font-display text-[34px] leading-[1.05] tracking-[-0.02em] sm:text-[44px]">
                  {t("routes.profile.briefTitle", { name: company.name })}
                </h2>
                <div className="mt-7 space-y-5 text-[16px] leading-[1.75] text-foreground/82">
                  {brief.paragraphs.length > 0 ? (
                    brief.paragraphs.slice(0, 4).map((p: string) => <p key={p}>{p}</p>)
                  ) : (
                    <p>{t("routes.profile.briefFallback", { name: company.name })}</p>
                  )}
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-3">
                  {[
                    {
                      label: t("routes.profile.industry"),
                      value: company.industry ?? t("routes.profile.unspecified"),
                    },
                    {
                      label: t("routes.profile.hq"),
                      value: company.country ?? t("routes.profile.global"),
                    },
                    {
                      label: t("routes.profile.size"),
                      value: company.size ?? t("routes.profile.notDisclosed"),
                    },
                  ].map((item) => (
                    <div key={item.label} className="border-l border-border pl-4">
                      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
                        {item.label}
                      </div>
                      <div className="mt-2 text-[15px] font-medium text-foreground">
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              <div className="grid gap-4 md:grid-cols-2">
                <section className="rounded-[18px] border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                  <div className="flex items-center gap-2 font-mono-eyebrow">
                    <Target className="h-3.5 w-3.5" /> {t("routes.profile.marketSignals")}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {brief.primarySignals.map((signal: string) => (
                      <span
                        key={signal}
                        className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[12px] text-foreground/84"
                      >
                        {signal}
                      </span>
                    ))}
                  </div>
                </section>

                <section className="rounded-[18px] border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                  <div className="flex items-center gap-2 font-mono-eyebrow">
                    <Sparkles className="h-3.5 w-3.5" /> {t("routes.profile.twinOutcomes")}
                  </div>
                  <div className="mt-5 space-y-3">
                    {brief.outcomes.slice(0, 4).map((outcome: string) => (
                      <div
                        key={outcome}
                        className="flex items-start gap-3 text-[13.5px] leading-relaxed text-foreground/82"
                      >
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                        <span>{outcome}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {brief.publicSections.length > 0 && (
                <section className="rounded-[18px] border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                  <div className="font-mono-eyebrow">{t("routes.profile.signalArchitecture")}</div>
                  <div className="mt-6 divide-y divide-border">
                    {brief.publicSections.slice(0, 5).map((s: ParsedSection) => (
                      <div
                        key={s.label}
                        className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[150px_1fr]"
                      >
                        <div className="text-[12px] uppercase tracking-[0.12em] text-muted-foreground font-mono">
                          {s.label}
                        </div>
                        <div className="text-[14px] leading-[1.65] text-foreground/82">
                          {s.chips ? (
                            <div className="flex flex-wrap gap-1.5">
                              {s.items!.slice(0, 10).map((it: string) => (
                                <span
                                  key={it}
                                  className="rounded-md border border-border bg-background px-2 py-1 text-[12px] text-foreground/80"
                                >
                                  {it}
                                </span>
                              ))}
                            </div>
                          ) : (
                            s.text
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Contact card */}
              <div className="relative rounded-[20px] border border-border bg-foreground text-background p-7 overflow-hidden">
                <div
                  className="absolute inset-0 opacity-[0.08] pointer-events-none"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
                    backgroundSize: "18px 18px",
                  }}
                />
                <div className="relative">
                  {leadSent ? (
                    <div className="text-center py-4">
                      <div className="mx-auto h-10 w-10 rounded-full bg-background text-foreground flex items-center justify-center">
                        <Check className="h-5 w-5" />
                      </div>
                      <div className="mt-4 font-serif text-xl">
                        {t("routes.profile.messageSent")}
                      </div>
                      <p className="mt-2 text-sm text-background/70">
                        {t("routes.profile.willReachOut", { name: company.name })}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={submitLead} className="space-y-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-background/60 flex items-center gap-2">
                          <Mail className="h-3 w-3" /> {t("routes.profile.directLine")}
                        </div>
                        <div className="mt-3 font-serif text-[26px] leading-[1.05] tracking-[-0.02em]">
                          {t("routes.profile.reachTitle1")}
                          <br />
                          {t("routes.profile.reachTitle2", { name: company.name })}
                        </div>
                        <p className="mt-3 text-[13px] text-background/60 leading-relaxed">
                          {t("routes.profile.leadSub", { name: company.name })}
                        </p>
                      </div>
                      <input
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        placeholder={t("routes.profile.phFullName")}
                        required
                        maxLength={120}
                        className="w-full rounded-xl bg-background/10 border border-background/20 text-background placeholder:text-background/50 px-4 py-2.5 text-sm focus:outline-none focus:border-background/60 transition"
                      />
                      <input
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        placeholder={t("routes.profile.phWorkEmail")}
                        type="email"
                        required
                        maxLength={200}
                        className="w-full rounded-xl bg-background/10 border border-background/20 text-background placeholder:text-background/50 px-4 py-2.5 text-sm focus:outline-none focus:border-background/60 transition"
                      />
                      <textarea
                        value={lead.message}
                        onChange={(e) => setLead({ ...lead, message: e.target.value })}
                        placeholder={t("routes.profile.phInterest")}
                        rows={3}
                        maxLength={1000}
                        className="w-full rounded-xl bg-background/10 border border-background/20 text-background placeholder:text-background/50 px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-background/60 transition"
                      />
                      <button
                        type="submit"
                        disabled={leadSending || !lead.name.trim() || !lead.email.trim()}
                        className="w-full rounded-full bg-background text-foreground px-4 py-3 text-sm font-medium hover:bg-background/90 disabled:opacity-50 transition"
                      >
                        {leadSending
                          ? t("routes.profile.sending")
                          : t("routes.profile.requestIntro")}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>

            <aside className="space-y-5 lg:sticky lg:top-6">
              <section className="overflow-hidden rounded-[22px] border border-border bg-card shadow-window">
                <div className="bg-foreground p-6 text-background">
                  <div className="flex items-start justify-between gap-5">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.16em] text-background/60 font-mono">
                        {t("routes.profile.twinOS")}
                      </div>
                      <div className="mt-3 font-display text-[30px] leading-[1.04] tracking-[-0.02em]">
                        {t("routes.profile.osTitle")}
                      </div>
                    </div>
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-background/18 bg-background/10 font-serif text-2xl">
                      {logo ? (
                        <img
                          src={logo}
                          alt={`${company.name} logo`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        initial
                      )}
                    </div>
                  </div>
                  <div className="mt-6 h-2 overflow-hidden rounded-full bg-background/12">
                    <div
                      className="h-full rounded-full bg-background"
                      style={{ width: `${readiness}%` }}
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[12px] text-background/62">
                    <span>{readinessLabel}</span>
                    <span className="tabular-nums">{readiness}/100</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 border-b border-border">
                  {metricCards.map((metric) => {
                    const Icon = metric.icon;
                    return (
                      <div
                        key={metric.label}
                        className="border-r border-b border-border p-5 even:border-r-0 [&:nth-last-child(-n+2)]:border-b-0"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <div className="mt-4 text-[28px] leading-none font-display tracking-[-0.02em]">
                          {metric.value}
                        </div>
                        <div className="mt-2 text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-mono">
                          {metric.label}
                        </div>
                        <div className="mt-1 text-[12px] text-foreground/64">{metric.detail}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="p-6">
                  <div className="space-y-3">
                    {operatingStack.map((row) => {
                      const Icon = row.icon;
                      return (
                        <div
                          key={row.label}
                          className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3"
                        >
                          <Icon className="h-4 w-4 shrink-0 text-foreground/70" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[12px] uppercase tracking-[0.12em] text-muted-foreground font-mono">
                              {row.label}
                            </div>
                            <div className="truncate text-[13.5px] text-foreground">
                              {row.value}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

              <section className="rounded-[22px] border border-border bg-card shadow-[var(--shadow-card)] overflow-hidden">
                <div className="border-b border-border px-5 py-4 flex items-center gap-3 bg-background/50">
                  <div className="h-9 w-9 overflow-hidden rounded-xl bg-foreground text-background flex items-center justify-center font-serif text-lg">
                    {logo ? (
                      <img
                        src={logo}
                        alt={`${company.name} logo`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      initial
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[14px] truncate">
                      {t("routes.profile.chatTitle", { name: company.name })}
                    </div>
                    <div className="text-[12px] text-muted-foreground flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-foreground animate-pulse" />
                      {t("routes.profile.liveEndpoint")}
                    </div>
                  </div>
                </div>
                <div ref={scrollRef} className="h-[430px] overflow-y-auto px-5 py-5 space-y-5">
                  {messages.length === 0 && (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-border bg-background p-5">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-mono">
                          <MessageSquare className="h-3.5 w-3.5" />{" "}
                          {t("routes.profile.inquiryDeck")}
                        </div>
                        <div className="mt-3 text-[18px] leading-snug font-display tracking-[-0.01em]">
                          {t("routes.profile.startFrom")}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {starterPrompts.map((s) => (
                          <button
                            key={s}
                            onClick={() => ask(s)}
                            className="group w-full rounded-xl border border-border bg-card px-4 py-3 text-left text-[13.5px] leading-snug text-foreground transition hover:border-foreground/30 hover:bg-background"
                          >
                            <span className="flex items-start justify-between gap-4">
                              <span>{s}</span>
                              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition group-hover:text-foreground" />
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
                        {brief.capabilities.slice(0, 3).map((capability: string) => (
                          <div
                            key={capability}
                            className="rounded-xl border border-border bg-background p-3 text-[12px] leading-snug text-foreground/72"
                          >
                            {capability}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {messages.map((m, mi) => {
                    const raw = m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
                    const isUser = m.role === "user";
                    if (isUser) {
                      return (
                        <div key={m.id} className="flex gap-3 justify-end">
                          <div className="max-w-[80%] rounded-2xl rounded-br-sm px-4 py-2.5 text-[14px] leading-[1.55] whitespace-pre-wrap bg-foreground text-background">
                            {raw}
                          </div>
                        </div>
                      );
                    }
                    const { text, followups } = splitFollowups(raw);
                    const sources = messageSources(m.metadata);
                    const isLastMessage = mi === messages.length - 1;
                    return (
                      <div key={m.id} className="space-y-2.5">
                        <div className="flex gap-3 justify-start">
                          <div className="h-8 w-8 overflow-hidden rounded-lg bg-foreground text-background flex items-center justify-center font-serif text-sm shrink-0">
                            {logo ? (
                              <img
                                src={logo}
                                alt={`${company.name} logo`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              initial
                            )}
                          </div>
                          <div className="max-w-[80%] rounded-2xl rounded-bl-sm px-4 py-2.5 text-[14px] leading-[1.55] bg-background border border-border text-foreground">
                            <Markdown text={text} sources={sources} />
                            <SourceChips text={text} sources={sources} />
                          </div>
                        </div>
                        {isLastMessage && !isLoading && followups.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pl-11">
                            {followups.map((f) => (
                              <button
                                key={f}
                                onClick={() => ask(f)}
                                className="text-[12px] px-3 py-1.5 rounded-full border border-border bg-card text-foreground/80 hover:border-foreground/30 hover:bg-background transition"
                              >
                                {f}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {isLoading && (
                    <div className="flex gap-3">
                      <div className="h-8 w-8 overflow-hidden rounded-lg bg-foreground text-background flex items-center justify-center font-serif text-sm">
                        {logo ? (
                          <img
                            src={logo}
                            alt={`${company.name} logo`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          initial
                        )}
                      </div>
                      <div className="rounded-2xl rounded-bl-sm bg-background border border-border px-4 py-3 flex gap-1.5 items-center">
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        />
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-bounce"
                          style={{ animationDelay: "120ms" }}
                        />
                        <span
                          className="h-1.5 w-1.5 rounded-full bg-foreground/40 animate-bounce"
                          style={{ animationDelay: "240ms" }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <form
                  onSubmit={submit}
                  className="border-t border-border p-4 flex gap-2 bg-background/40"
                >
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={t("routes.profile.askAnything", { name: company.name })}
                    className="flex-1 rounded-full bg-background border border-border px-4 py-2.5 text-sm focus:outline-none focus:border-foreground/40 transition"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="rounded-full bg-foreground text-background px-5 py-2.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 hover:opacity-90 transition"
                  >
                    <Send className="h-3.5 w-3.5" /> {t("routes.profile.send")}
                  </button>
                </form>
              </section>
            </aside>
          </div>
        </div>
      </section>
    </Layout>
  );
}
